/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useCallback } from 'react';
import { useSpring } from 'react-spring';
import { isNullOrEmptyArray, calculateArrayHashCode } from '../../util/arrays';
import { Loading } from '../Loading';
import * as Ui from './styles';

const SUBITEM_MIN_HEIGHT = 28;

/**
 * Componente que possibilita criar uma listagem de itens ordenáveis
 * Exemplo de uso.
 *
 * <CollapsibleOrderableList
 *   title="Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"
 *   checkedTitle={true}
 *   boldedTitle={false}
 *   reorderable={false}
 *   collapsed={false}
 *   data={[
 *     {value: 1, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *     {value: 2, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *     {value: 3, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *     {value: 4, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *     {value: 5, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *     {value: 6, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *     {value: 7, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *   ]}
 *   dataToMath={[
 *     {value: 1, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus", order: 1},
 *     {value: 2, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus", order: 2},
 *     {value: 3, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus", order: 3},
 *     {value: 4, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus", order: 4},
 *     {value: 5, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus", order: 5},
 *     {value: 6, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus", order: 6},
 *     {value: 7, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus", order: 7},
 *   ]}
 *   initialOrder={[3, 2, 1, 4, 5, 6, 7]}
 * />
 * @param {*} param0
 */
function CollapsibleOrderableListComp({
  // título da lista
  title,
  // define se o título inicia recolhido
  collapsed = true,
  // define se o título está selecionado
  checkedTitle = false,
  // define se o título está em modo somente leitura (não modifica o estado inicial do título)
  readOnlyTitle = false,
  // define se o título tem destaque
  boldedTitle = false,
  // define se os itens podem ser checados
  reorderable = false,
  // define se apresenta um loading ao invés de dados quando o componente estiver expandido
  loading = false,
  // dados a serem apresentados.Ex.: [{value: 1, label: "Lorem ipsum dolor", order: 4, manual: true}]
  data = [],
  // dados a serem combinados com "data". Ex.: [{value: 1, label: "Lorem ipsum dolor", order: 4}]
  dataToMatch = [],
  // evento de propagação de seleção do título
  onSelectTitle,
  // evento de propagação da ação de expandir
  onExpand,
  // propagação de evento de alteração das combinações de "dataToMatch" para "data"
  // [{source: dataToMatchItem.value, matched: dataItem.value}]
  onChangeMatchs,
}) {
  // states
  const [autoExpanded, setAutoExpanded] = useState(false);
  // const [isSelectedTitle, setIsSelectedTitle] = useState(false);
  const [itemsSet, setItemsSet] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [bodyVisibility, setBodyVisibility] = useState(!collapsed);
  const dataGroupQt = loading ? 3 : Math.round(data.length / 3) + 1;
  // variável que controla qual subItem esta aberto
  const [openItem, setOpenItem] = useState(-1);

  // callbacks
  const fireMatchsEvent = useCallback((itemsSetLocal) => {
    // [{source from (dataToMatch), matched from (data)}]
    const matchs = itemsSetLocal.map((s) => {
      const fromIndex = s.order - 1;
      const fromValue = dataToMatch[fromIndex] ? dataToMatch[fromIndex].value : null;
      return { source: fromValue, matched: s.value };
    });
    // notify listener
    if (onChangeMatchs) {
      onChangeMatchs(matchs);
    }
  }, [dataToMatch, onChangeMatchs]);

  // update checkedTitle
  /* useEffect(() => {
    setIsSelectedTitle(checkedTitle);
  }, [checkedTitle]); */

  // effect on input data. execute only one time.
  useEffect(() => {
    // Verificamos se o array de data é vazio (ele possui os matchers)
    const dataIsEmpty = isNullOrEmptyArray(data);
    // Verificamos se o array itemsSet é vazio (ele possui os itens que são consumidos no componente de match de alternativas)
    const itemsSetIsEmpty = isNullOrEmptyArray(itemsSet);

    // Verificamos se o array de data possui algum match identificado
    const dataHasSomeOrderDistributed = !dataIsEmpty
      && data.some((match) => match.order !== null);

    // Verificamos se o array de itemsSet não possui nenhum match configurado, ou seja, todos
    // estão setados em nulo.
    const itemsSetHasNoOrderDistributed = !itemsSetIsEmpty
      && itemsSet.filter((match) => match.order == null).length === itemsSet.length;
    
    // Adicionamos uma verificação para que o setState do ItemSet seja feito caso o array de data possua
    // ordens distribuidas e o array itemsSet não possua. Isso representa uma falha de sincronismo, e este
    // era o motivo pelo qual os matches não eram setados inicialmente, referente a TC-1195
    if (!dataIsEmpty && (itemsSetIsEmpty || (dataHasSomeOrderDistributed && itemsSetHasNoOrderDistributed))) {
      const itemsSetHash = calculateArrayHashCode(itemsSet);
      const dataHash = calculateArrayHashCode(data);
      if (dataHash !== itemsSetHash) {
        // initializing items set
        setItemsSet(data);
        // fire event
        fireMatchsEvent(data);
      }
    }
  }, [data, itemsSet, fireMatchsEvent]);

  // efeito de expandir caso boldedTitle seja verdadeira
  useEffect(() => {
    if (boldedTitle && !autoExpanded) {
      setIsCollapsed(false);
      setAutoExpanded(true);
      if (onExpand) onExpand();
    }
  }, [boldedTitle, autoExpanded, onExpand]);

  // collapse or expand
  const collapseAnimation = useSpring({
    config: { duration: 100 },
    from: {
      height: isCollapsed ? `${dataGroupQt * SUBITEM_MIN_HEIGHT + 5}px` : '0px',
    },
    to: {
      height: isCollapsed ? '0px' : `${dataGroupQt * SUBITEM_MIN_HEIGHT + 5}px`,
    },
    onStart: () => {
      if (!isCollapsed) setBodyVisibility(true);
    },
    onRest: () => {
      if (isCollapsed) setBodyVisibility(false);
    },
  }, [isCollapsed]);

  // handle selection of title
  function handleSelectTitle() {
    if (boldedTitle || readOnlyTitle) return;
    const selected = !checkedTitle;
    // setIsSelectedTitle(selected);
    if (onSelectTitle) onSelectTitle(selected);
  }

  // handle touch in head title
  function handleCollapse() {
    setIsCollapsed(!isCollapsed);
    if (isCollapsed) {
      if (onExpand) onExpand();
    }
  }

  // handle change order in data (items set)
  // chamado quando se muda a ordem das alternativas manualmente
  function handleChangeOrder(itemValue, oldOrder, newOrder) {
    // change order
    const newItemsSet = [];
    for (let index = 0; index < itemsSet.length; index += 1) {
      const currentItem = itemsSet[index];

      if (!newOrder) {
        if (currentItem.value === itemValue) {
          newItemsSet[index] = { ...currentItem, order: newOrder };
        } else {
          newItemsSet[index] = currentItem;
        }
      } else if (currentItem.order === newOrder) {
        newItemsSet[index] = { ...currentItem, order: oldOrder };
      } else if (currentItem.value === itemValue) {
        newItemsSet[index] = { ...currentItem, order: newOrder, manual: true };
      } else {
        newItemsSet[index] = currentItem;
      }
    }

    setItemsSet(newItemsSet);
    // calculate matchs
    fireMatchsEvent(newItemsSet);
  }

  const WrappedItem = (wrappedProps) => {
    // inner item
    const InnerItem = (innerProps) => {
      const textOrder = innerProps.order ? (`${innerProps.order} -`).padStart(4, '0') : '';

      return (
        <Ui.TextBox colored={innerProps.colored}>
          <Ui.TextOrder>{textOrder}</Ui.TextOrder>
          <Ui.TextGroup>
            <Ui.Text className="CollapsibleOrderableList_Text">
              <Ui.TextWithHint controlId={innerProps.controlId}>
                {innerProps.label}
              </Ui.TextWithHint>
            </Ui.Text>
            {innerProps.withArrowDown && (<Ui.ArrowDown size={15} />)}
          </Ui.TextGroup>
        </Ui.TextBox>
      );
    };

    // result
    return (reorderable ? (
      // perguntas a serem evoluidas, por exemplo
      <Ui.NumbersBoxPopup
        hint={wrappedProps.hint}
        names={dataToMatch}
        selectedValue={wrappedProps.order}
        onSelectItem={(newOrder) => handleChangeOrder(wrappedProps.value, wrappedProps.order, newOrder)}
        setOpenItem={setOpenItem}
        openItem={openItem === wrappedProps.index}
        showCleanOption
      >
        <Ui.Item className={'clickable'} {...wrappedProps} clear>
          <InnerItem
            controlId={wrappedProps.id}
            order={wrappedProps.order}
            label={wrappedProps.label}
            withArrowDown={wrappedProps.withArrowDown}
            colored={wrappedProps.colored}
          />
        </Ui.Item>
      </Ui.NumbersBoxPopup>
    ) : (
    // Pergunta principal do evolutivo, por exemplo
      <Ui.FixedItem {...wrappedProps}>
        <InnerItem
          controlId={wrappedProps.id}
          order={wrappedProps.order}
          label={wrappedProps.label}
          withArrowDown={wrappedProps.withArrowDown}
        />
      </Ui.FixedItem>
    ));
  };

  // renders: loading
  function renderLoadingSpinner() {
    return (
      <Ui.ItemsEmpty>
        <Loading size={30} running={loading} />
      </Ui.ItemsEmpty>
    );
  }

  return (
    <Ui.Container isChecked={checkedTitle} isCollapsed={isCollapsed}>
      <Ui.Head>
        <Ui.CircleWrapper clear onClick={() => handleSelectTitle()}>
          <Ui.Circle size={15} isBolded={boldedTitle} isChecked={checkedTitle} />
        </Ui.CircleWrapper>
        <Ui.Collapser clear onClick={() => handleCollapse()}>
          <Ui.Arrow size={18} color={'#610082'} isChecked={checkedTitle} />
          <Ui.Title isBolded={boldedTitle}>{title}</Ui.Title>
        </Ui.Collapser>
      </Ui.Head>
      <Ui.Body style={{ ...collapseAnimation, display: bodyVisibility ? 'flex' : 'none' }}>
        {loading && !isCollapsed ? renderLoadingSpinner() : (
          <Ui.Items isOpen={!isCollapsed}>
            {itemsSet.map((item, index) => (
              <WrappedItem
                key={`id-${item.value}`}
                id={`id-${item.value}`}
                value={item.value}
                index={index}
                order={item.order}
                orderText={(`${item.order} -`).padStart(4, '0')}
                hint={item.label}
                label={item.label}
                withArrowDown={reorderable}
                colored={item.manual}
                onClick={() => setOpenItem(index)}
              />
            ))}
          </Ui.Items>
        )}
      </Ui.Body>
    </Ui.Container>
  );
}

export const CollapsibleOrderableList = React.memo(CollapsibleOrderableListComp);
